import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navigation from './Navigation';
import api from '../config/api'; import './common.css'; import './ProductList.css';

function ProductDetail() {
    const { id } = useParams();
    const { logout } = useAuth();
    const navigate = useNavigate();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [editMode, setEditMode] = useState(false);
    const [editData, setEditData] = useState({
        amazon_sku: '',
        paused: false
    });

    useEffect(() => {
        fetchProduct();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const fetchProduct = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await api.get(`/api/products/${id}`);
            if (response.data.success) {
                const productData = response.data.data;
                setProduct(productData);
                setEditData({
                    amazon_sku: productData.amazon_sku,
                    paused: productData.paused
                });
            }
        } catch (err) {
            if (err.response?.status === 401) {
                logout();
                navigate('/login');
            } else if (err.response?.status === 404) {
                setError('Product not found');
            } else {
                setError('Failed to fetch product details');
            }
            console.error('Error fetching product:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleEditChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEditData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        try {
            const response = await api.put(`/api/products/${id}`, editData);
            if (response.data.success) {
                setSuccess('Product updated successfully');
                setProduct(response.data.data);
                setEditMode(false);
                setTimeout(() => setSuccess(''), 3000);
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to update product');
            setTimeout(() => setError(''), 3000);
        }
    };

    const handleDelete = async () => {
        const confirmed = window.confirm(
            `Are you sure you want to delete this product? This action cannot be undone.`
        );

        if (!confirmed) return;

        try {
            const response = await api.delete(`/api/products/${id}`);
            if (response.data.success) {
                setSuccess('Product deleted successfully');
                setTimeout(() => {
                    navigate('/products');
                }, 1500);
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to delete product');
            setTimeout(() => setError(''), 3000);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    const formatPrice = (price) => {
        if (!price) return 'N/A';
        return `$${parseFloat(price).toFixed(2)}`;
    };

    if (loading) {
        return (
            <div className="page-container">
                <Navigation />
                <div className="page-content">
                    <div className="loading">Loading product details...</div>
                </div>
            </div>
        );
    }

    if (error && !product) {
        return (
            <div className="page-container">
                <Navigation />
                <div className="page-content">
                    <div className="alert alert-error">{error}</div>
                    <button className="btn btn-primary" onClick={() => navigate('/products')}>
                        Back to Products
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="page-container">
            <Navigation />

            <div className="page-content">
                <div className="page-header">
                    <h2>Product Details</h2>
                    <button
                        className="btn btn-secondary"
                        onClick={() => navigate('/products')}
                    >
                        ← Back to Products
                    </button>
                </div>

                {error && <div className="alert alert-error">{error}</div>}
                {success && <div className="alert alert-success">{success}</div>}

                {product && (
                    <div className="detail-card">
                        {!editMode ? (
                            <div className="detail-actions">
                                <button
                                    className="btn btn-edit"
                                    onClick={() => setEditMode(true)}
                                >
                                    Edit Product
                                </button>
                                <button
                                    className="btn btn-delete"
                                    onClick={handleDelete}
                                >
                                    Delete Product
                                </button>
                            </div>
                        ) : (
                            <div className="detail-actions">
                                <button
                                    className="btn btn-secondary"
                                    onClick={() => {
                                        setEditMode(false);
                                        setEditData({
                                            amazon_sku: product.amazon_sku,
                                            paused: product.paused
                                        });
                                    }}
                                >
                                    Cancel Edit
                                </button>
                            </div>
                        )}

                        {editMode ? (
                            <form onSubmit={handleUpdate} className="edit-form">
                                <div className="detail-section">
                                    <h2>Edit Product</h2>
                                    <div className="form-group">
                                        <label htmlFor="amazon_sku">Amazon SKU</label>
                                        <input
                                            type="text"
                                            id="amazon_sku"
                                            name="amazon_sku"
                                            value={editData.amazon_sku}
                                            onChange={handleEditChange}
                                            required
                                        />
                                    </div>
                                    <div className="form-group checkbox-group">
                                        <label>
                                            <input
                                                type="checkbox"
                                                name="paused"
                                                checked={editData.paused}
                                                onChange={handleEditChange}
                                            />
                                            <span>Pause Product</span>
                                        </label>
                                    </div>
                                    <button type="submit" className="btn btn-primary">
                                        Save Changes
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <>
                                <div className="detail-section">
                                    <h2>Basic Information</h2>
                                    <div className="detail-grid">
                                        <div className="detail-item">
                                            <span className="label">Product ID:</span>
                                            <span className="value">{product.id}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="label">Title:</span>
                                            <span className="value">{product.title || 'N/A'}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="label">eBay ID:</span>
                                            <span className="value">{product.ebay_id}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="label">eBay Variation SKU:</span>
                                            <span className="value">{product.ebay_variation_sku || 'N/A'}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="label">Amazon SKU:</span>
                                            <span className="value">{product.amazon_sku}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="detail-section">
                                    <h2>Pricing & Inventory</h2>
                                    <div className="detail-grid">
                                        <div className="detail-item">
                                            <span className="label">Price:</span>
                                            <span className="value">{formatPrice(product.price)}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="label">Quantity:</span>
                                            <span className="value">{product.quantity}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="label">Shipping Cost:</span>
                                            <span className="value">{formatPrice(product.shipping_cost)}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="label">Shipping Time:</span>
                                            <span className="value">{product.shipping_time || 'N/A'}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="detail-section">
                                    <h2>Status & Seller</h2>
                                    <div className="detail-grid">
                                        <div className="detail-item">
                                            <span className="label">Seller ID:</span>
                                            <span className="value">{product.seller_id || 'N/A'}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="label">Listing Status:</span>
                                            <span className={`status-badge ${product.listing_status?.toLowerCase()}`}>
                                                {product.listing_status || 'UNKNOWN'}
                                            </span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="label">Paused:</span>
                                            <span className={`pause-badge ${product.paused ? 'paused' : 'active'}`}>
                                                {product.paused ? 'Yes' : 'No'}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {product.ebay_product_url && (
                                    <div className="detail-section">
                                        <h2>Links</h2>
                                        <div className="detail-item">
                                            <a
                                                href={product.ebay_product_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="external-link"
                                            >
                                                View on eBay →
                                            </a>
                                        </div>
                                    </div>
                                )}

                                <div className="detail-section">
                                    <h2>Timestamps</h2>
                                    <div className="detail-grid">
                                        <div className="detail-item">
                                            <span className="label">Created At:</span>
                                            <span className="value">{formatDate(product.created_at)}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="label">Updated At:</span>
                                            <span className="value">{formatDate(product.updated_at)}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="label">Last Amazon Update:</span>
                                            <span className="value">{formatDate(product.last_amazon_update_at)}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="label">Amazon Update Failures:</span>
                                            <span className="value">{product.amazon_update_failure_count ?? 0}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="label">Last Quantity Check:</span>
                                            <span className="value">{formatDate(product.last_ebay_quantity_checked_at)}</span>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default ProductDetail;
